import { useEffectOnce, useSubsequentEffect } from '@carvana/merch-react-hooks';
import { useContext } from 'react';
import { ALERT_SIGN_UP_VEHICLES } from '~/constants/vdp/signUpForAlerts';
import UserVehicleContext from '~/context/vdp/UserVehicleContext';
import { useGetVehicleId } from '~/context/vdp/VehicleContext';
import {
  loadItemFromLocalStorage,
  saveItemToLocalStorage
} from '~/utilities/localStorage';

export type AlertSignUpVehicles = {
  [vehicleId: number]: string;
};

const ninetyDays = 90 * 24 * 60 * 60 * 1000;

// Handles the subscription to vehicle availability notifications/alerts.
// SignUpForAlertsModal.tsx is responsible for adding new vehicles to state
// and this hook is responsible for listening for that change and saving the
// subscription to local storage
export default function useLocalStorageVehicleSubscription() {
  const { isSubscribedToNotifications, setSubscribedToNotifications } =
    useContext(UserVehicleContext);
  const vehicleId = useGetVehicleId();

  useEffectOnce(() => {
    const currentDate = new Date();
    const vehiclesFromStorage =
      loadItemFromLocalStorage<AlertSignUpVehicles>(ALERT_SIGN_UP_VEHICLES) ??
      {};

    const isSubscribedFromStorage =
      Boolean(vehiclesFromStorage[vehicleId]) &&
      new Date(vehiclesFromStorage[vehicleId]) >= new Date();

    // while we're here, filter out any expired vehicles
    const vehicles = Object.fromEntries(
      Object.entries(vehiclesFromStorage).filter(
        ([_, expirationDate]) => new Date(expirationDate) >= currentDate
      )
    );
    saveItemToLocalStorage(ALERT_SIGN_UP_VEHICLES, vehicles);

    setSubscribedToNotifications(v => v || isSubscribedFromStorage);
  }, [setSubscribedToNotifications, vehicleId]);

  useSubsequentEffect(() => {
    const vehicleSubscriptions =
      loadItemFromLocalStorage<AlertSignUpVehicles>(ALERT_SIGN_UP_VEHICLES) ??
      {};

    if (isSubscribedToNotifications) {
      saveItemToLocalStorage(ALERT_SIGN_UP_VEHICLES, {
        [vehicleId]: new Date(Date.now() + ninetyDays),
        ...vehicleSubscriptions
      });
    } else if (vehicleSubscriptions[vehicleId]) {
      const { [vehicleId]: ignored, ...remainingSubscriptions } =
        vehicleSubscriptions;
      saveItemToLocalStorage(ALERT_SIGN_UP_VEHICLES, remainingSubscriptions);
    }
  }, [isSubscribedToNotifications, vehicleId]);
}
